import { useState, type ReactNode } from 'react'
import {
    useFloating,
    autoUpdate,
    offset,
    flip,
    shift,
    useHover,
    useFocus,
    useDismiss,
    useRole,
    useInteractions,
    FloatingPortal,
    safePolygon
} from '@floating-ui/react'
import { motion, AnimatePresence } from 'framer-motion'
import { AgentHoverCard } from './AgentHoverCard'
import type { Agent } from '../../../services/agentService'
import { Z_INDEX } from '../../../constants/zIndex'

interface AgentTooltipTriggerProps {
    agent: Agent
    children: ReactNode
    className?: string
}

export const AgentTooltipTrigger = ({ agent, children, className }: AgentTooltipTriggerProps) => {
    const [isOpen, setIsOpen] = useState(false)

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        strategy: 'fixed', // Better for scrolling
        middleware: [
            offset(12),
            flip({
                fallbackAxisSideDirection: 'start',
                crossAxis: false // Prevent flipping to unrelated axis if possible
            }),
            shift({ padding: 10 }) // Boundary protection padding
        ],
        whileElementsMounted: autoUpdate,
        placement: 'bottom-start'
    })

    const hover = useHover(context, {
        move: false,
        mouseOnly: true, // CRITICAL: Disable on touch devices to prevent blocking clicks
        delay: { open: 200, close: 0 }, // Small delay to prevent accidental triggers
        handleClose: safePolygon()
    })

    const focus = useFocus(context)
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'tooltip' })

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        focus,
        dismiss,
        role
    ])

    return (
        <>
            <div
                ref={refs.setReference}
                {...getReferenceProps()}
                className={className}
            >
                {children}
            </div>

            <FloatingPortal>
                <AnimatePresence>
                    {isOpen && (
                        <div
                            ref={refs.setFloating}
                            style={{
                                ...floatingStyles,
                                zIndex: Z_INDEX.TOOLTIP
                            }}
                            {...getFloatingProps()}
                        >
                            <motion.div
                                {...({
                                    initial: { opacity: 0, scale: 0.95, y: 10 },
                                    animate: { opacity: 1, scale: 1, y: 0 },
                                    exit: { opacity: 0, scale: 0.95, y: 10 }
                                } as any)}
                                transition={{ duration: 0.2 }}
                            >
                                <AgentHoverCard agent={agent} showOptions={false} />
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>
            </FloatingPortal>
        </>
    )
}
